import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Input,
  InputGroup,
  InputRightElement,
  Box,
  List,
  ListItem,
  useToast,
  Spinner,
  Text,
  IconButton,
  Tooltip
} from '@chakra-ui/react';
import { SearchIcon, CloseIcon } from '@chakra-ui/icons';
import useGoogleMapsLoader from '../hooks/useGoogleMapsLoader';
import config from '../config';

const AddressSearch = ({ onSelect, onClear, postcode, ...props }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const toast = useToast();
  
  // Use the Google Maps loader hook
  const { googleMapsLoaded, searchInputRef } = useGoogleMapsLoader();
  const autocompleteRef = useRef(null);
  const placesServiceRef = useRef(null);

  // Initialize Google Places Autocomplete
  useEffect(() => {
    if (!googleMapsLoaded || !searchInputRef.current) return;

    let isMounted = true;
    let autocomplete = null;

    const initializeAutocomplete = () => {
      try {
        if (!window.google || !window.google.maps || !window.google.maps.places) {
          console.log('Google Maps API not fully loaded, retrying...');
          setTimeout(initializeAutocomplete, 100);
          return;
        }

        // Create the autocomplete instance
        autocomplete = new window.google.maps.places.Autocomplete(
          searchInputRef.current,
          {
            componentRestrictions: { country: 'gb' },
            fields: ['address_components', 'geometry', 'formatted_address', 'place_id'],
            types: ['address']
          }
        );

        // Set up place changed listener
        autocomplete.addListener('place_changed', () => {
          if (!isMounted) return;
          
          const place = autocomplete.getPlace();
          if (!place || !place.address_components) {
            toast({
              title: 'Error',
              description: 'No address details available for the selected location',
              status: 'error',
              duration: 3000,
              isClosable: true,
            });
            return;
          }


          // Format the address data to match your application's expected format
          const address = formatAddressFromComponents(place.address_components);
          
          onSelect({
            id: place.place_id,
            address: address,
            postcode: address.postcode,
            latitude: place.geometry?.location?.lat(),
            longitude: place.geometry?.location?.lng(),
            formattedAddress: place.formatted_address
          });
          
          setQuery(place.formatted_address);
          setIsOpen(false);
        });

        // Initialize places service for search box predictions
        const placesService = new window.google.maps.places.PlacesService(
          document.createElement('div')
        );
        
        if (isMounted) {
          autocompleteRef.current = autocomplete;
          placesServiceRef.current = placesService;
        }
      } catch (error) {
        console.error('Error initializing Google Places:', error);
        if (isMounted) {
          toast({
            title: 'Error',
            description: 'Failed to initialize address search. Please try again.',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
        }
      }
    };

    // Initial attempt to initialize
    initializeAutocomplete();

    // Cleanup function
    return () => {
      isMounted = false;
      if (autocomplete) {
        window.google.maps.event.clearInstanceListeners(autocomplete);
      }
    };
  }, [googleMapsLoaded, onSelect, toast]);

  // Format address components from Google Places API
  const formatAddressFromComponents = (components) => {
    let address = {
      line1: '',
      line2: '',
      line3: '',
      line4: '',
      town: '',
      county: '',
      postcode: '',
      country: ''
    };

    components.forEach(component => {
      const types = component.types;
      const value = component.long_name;

      if (types.includes('street_number')) {
        address.line1 = value;
      } else if (types.includes('route')) {
        address.line1 = address.line1 ? `${address.line1} ${value}` : value;
      } else if (types.includes('sublocality_level_1') || types.includes('neighborhood')) {
        address.line2 = value;
      } else if (types.includes('postal_town') || types.includes('locality')) {
        address.town = value;
      } else if (types.includes('administrative_area_level_2')) {
        address.county = value;
      } else if (types.includes('postal_code')) {
        address.postcode = value;
      } else if (types.includes('country')) {
        address.country = value;
      }
    });

    // Format the full address
    address.formattedAddress = [
      address.line1,
      address.line2,
      address.town,
      address.county,
      address.postcode
    ].filter(Boolean).join(', ');

    return address;
  };

  // Handle input changes for search-as-you-type
  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    
    if (!value) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    if (value.length < 3) {
      setSuggestions([]);
      return;
    }

    // Ensure Google Maps API is loaded
    if (!googleMapsLoaded) {
      console.log('Google Maps API not loaded yet, queuing search...');
      // Queue the search for when the API is ready
      const checkApiLoaded = setInterval(() => {
        if (googleMapsLoaded && window.google?.maps?.places) {
          clearInterval(checkApiLoaded);
          // Re-run the search with the current value
          handleInputChange({ target: { value } });
        }
      }, 100);
      return;
    }

    try {
      // Use Google Places AutocompleteService for search-as-you-type
      const service = new window.google.maps.places.AutocompleteService();
      
      service.getPlacePredictions(
        {
          input: value,
          componentRestrictions: { country: 'gb' },
          types: ['address']
        },
        (predictions, status) => {
          if (status !== window.google.maps.places.PlacesServiceStatus.OK || !predictions) {
            setSuggestions([]);
            return;
          }
          
          const formattedPredictions = predictions.map(prediction => ({
            id: prediction.place_id,
            address: prediction.description,
            terms: prediction.terms
          }));
          
          setSuggestions(formattedPredictions);
          setIsOpen(true);
        }
      );
    } catch (error) {
      console.error('Error getting place predictions:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch address suggestions',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle manual selection from dropdown
  const handleSelectSuggestion = (suggestion) => {
    if (!googleMapsLoaded) {
      toast({
        title: 'Loading...',
        description: 'Address service is still initializing. Please try again in a moment.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    if (!window.google?.maps?.places) {
      toast({
        title: 'Error',
        description: 'Address service not available. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Use the place ID to get full details
      const service = new window.google.maps.places.PlacesService(document.createElement('div'));
      
      service.getDetails(
        { placeId: suggestion.id, fields: ['address_components', 'geometry', 'formatted_address'] },
        (place, status) => {
          setIsLoading(false);
          
          if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) {
            toast({
              title: 'Error',
              description: 'Could not retrieve address details',
              status: 'error',
              duration: 3000,
              isClosable: true,
            });
            return;
          }
          
          const address = formatAddressFromComponents(place.address_components);
          
          onSelect({
            id: place.place_id,
            address: address,
            postcode: address.postcode,
            latitude: place.geometry?.location?.lat(),
            longitude: place.geometry?.location?.lng(),
            formattedAddress: place.formatted_address || suggestion.address
          });
          
          setQuery(place.formatted_address || suggestion.address);
          setIsOpen(false);
        }
      );
    } catch (error) {
      console.error('Error getting place details:', error);
      setIsLoading(false);
      toast({
        title: 'Error',
        description: 'Failed to fetch address details',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleClear = () => {
    setQuery('');
    setSuggestions([]);
    setIsOpen(false);
    if (onClear) onClear();
  };

  return (
    <Box position="relative" width="100%" ref={containerRef} {...props}>
      <InputGroup>
        <Input
          ref={searchInputRef}
          placeholder={!googleMapsLoaded 
            ? "Loading address service..." 
            : postcode 
              ? `Search for an address in ${postcode}...` 
              : "Search for any UK address..."
          }
          value={query}
          onChange={handleInputChange}
          onFocus={() => query.length >= 3 && setIsOpen(true)}
          pr="4.5rem"
          autoComplete="off"
          isDisabled={!googleMapsLoaded}
        />
        <InputRightElement width="4.5rem">
          {!googleMapsLoaded ? (
            <Spinner size="sm" />
          ) : isLoading ? (
            <Spinner size="sm" />
          ) : query ? (
            <Tooltip label="Clear search">
              <IconButton
                h="1.75rem"
                size="sm"
                icon={<CloseIcon />}
                onClick={handleClear}
                aria-label="Clear search"
                variant="ghost"
              />
            </Tooltip>
          ) : (
            <SearchIcon color="gray.400" />
          )}
        </InputRightElement>
      </InputGroup>
      
      {isOpen && suggestions.length > 0 && (
        <Box
          position="absolute"
          zIndex="dropdown"
          width="100%"
          mt={1}
          bg="white"
          boxShadow="md"
          borderRadius="md"
          borderWidth="1px"
          maxH="300px"
          overflowY="auto"
        >
          <List spacing={0}>
            {suggestions.map((suggestion, index) => {
              // Format the suggestion text to show the main text and the area in a muted color
              const mainText = suggestion.terms && suggestion.terms[0] ? suggestion.terms[0].value : '';
              const secondaryText = suggestion.terms && suggestion.terms.length > 1 
                ? suggestion.terms.slice(1).map(term => term.value).join(', ')
                : '';
                
              return (
                <ListItem
                  key={suggestion.id || index}
                  p={2}
                  _hover={{ bg: 'gray.100', cursor: 'pointer' }}
                  onClick={() => handleSelectSuggestion(suggestion)}
                  borderBottomWidth={index < suggestions.length - 1 ? '1px' : '0'}
                  borderColor="gray.100"
                >
                  <Text fontWeight="medium">{mainText}</Text>
                  {secondaryText && (
                    <Text fontSize="sm" color="gray.500">{secondaryText}</Text>
                  )}
                </ListItem>
              );
            })}
          </List>
        </Box>
      )}
      
      {/* Hidden input for Google's autocomplete dropdown */}
      <input
        type="text"
        ref={inputRef}
        style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
        aria-hidden="true"
      />
    </Box>
  );
};

export default AddressSearch;
